import { ref, watch, onMounted, onBeforeUnmount, nextTick } from "vue";

export function useInfiniteScroll({ fetchPage, limit = 150, extraParams = null }) {
	const items = ref([]);
	const isLoading = ref(true);
	const isLoadingMore = ref(false);
	const hasMore = ref(true);
	const totalEntries = ref(0);
	const lastResponse = ref(null);
	const sentinelRef = ref(null);

	let page = 1;
	let requestId = 0;
	let observer = null;

	const currentParams = () => (extraParams && extraParams.value) ? extraParams.value : {};

	async function loadPage(pageToLoad, replace) {
		const id = ++requestId;
		if (replace) isLoading.value = true;
		else isLoadingMore.value = true;

		try {
			const response = await fetchPage(pageToLoad, limit, currentParams());
			if (id !== requestId) return;

			lastResponse.value = response;
			const { items: newItems, totalEntries: total, totalPages } = response;

			items.value = replace ? newItems : items.value.concat(newItems);
			totalEntries.value = total || 0;
			hasMore.value = pageToLoad < (totalPages || 0);
			page = pageToLoad;
		}
		finally {
			if (id === requestId) {
				isLoading.value = false;
				isLoadingMore.value = false;
			}
		}
	}

	async function reset() {
		page = 1;
		items.value = [];
		hasMore.value = true;
		await loadPage(1, true);
	}

	async function loadMore() {
		if (!hasMore.value || isLoadingMore.value || isLoading.value) return;
		await loadPage(page + 1, false);
	}

	function setupObserver() {
		if (observer) observer.disconnect();
		if (!sentinelRef.value) return;
		observer = new IntersectionObserver((entries) => {
			if (entries.some(e => e.isIntersecting)) loadMore();
		}, { rootMargin: "200px" });
		observer.observe(sentinelRef.value);
	}

	watch(sentinelRef, async () => {
		await nextTick();
		setupObserver();
	});

	if (extraParams) {
		watch(extraParams, () => { reset(); }, { deep: true });
	}

	onMounted(() => { loadPage(1, true); });
	onBeforeUnmount(() => { if (observer) observer.disconnect(); });

	return {
		items,
		isLoading,
		isLoadingMore,
		hasMore,
		totalEntries,
		lastResponse,
		sentinelRef,
		reset,
		loadMore
	};
}
